/** 激励广告语言整份覆盖及两用途单元登记；沿用 App 配置的卡片、表单与弹窗组件。 */
import { RewardedAdConfigSchema, UpsertRewardedAdUnitRequestSchema } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { AdminApiError } from '~/api/errors';
import {
  type RewardedAdConfig,
  type RewardedAdLanguageConfigResponse,
  type RewardedAdPlacement,
  type RewardedAdUnitResponse,
  appQueryKeys,
  getApp,
  rewardedAdQueryKeys,
  rewardedAdsApi,
  useRewardedAdLanguageConfigs,
  useRewardedAdUnits,
} from '~/data/queries';
import { fmtDateTime, fmtLang } from '~/lib/format';
import { useAuthStore } from '~/stores/auth-store';
import { ConfirmDialog, Dialog, DialogBody } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { Section } from '~/ui/Section';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
import {
  RewardedAdConfigFields,
  type RewardedAdFieldErrors,
  rewardedAdConfigFromDraft,
  rewardedAdConfigToDraft,
} from './rewarded-ad-config-fields';

const PLACEMENT_LABELS: Record<RewardedAdPlacement, string> = {
  TASK_CENTER: '任务中心',
  CHAPTER_UNLOCK: '章节解锁',
};

function fieldErrorsFromError(error: unknown): RewardedAdFieldErrors {
  return error instanceof AdminApiError
    ? Object.fromEntries((error.errors ?? []).map(({ field, message }) => [field, message]))
    : {};
}

export function RewardedAdsPanel({
  appId,
  platform,
  allowedLanguages,
  defaultConfig,
  hasUnsavedAppChanges = false,
}: {
  appId: string;
  platform: 'IOS' | 'ANDROID' | 'WEB';
  allowedLanguages: string[];
  defaultConfig: RewardedAdConfig;
  hasUnsavedAppChanges?: boolean;
}) {
  const role = useAuthStore((state) => state.user?.role);
  const canWrite = role === 'root' || role === 'admin';
  const languageQuery = useRewardedAdLanguageConfigs(appId);
  const unitQuery = useRewardedAdUnits(appId, platform !== 'WEB');
  const queryClient = useQueryClient();
  const unsavedAppChangesRef = useRef(hasUnsavedAppChanges);
  unsavedAppChangesRef.current = hasUnsavedAppChanges;
  const [preparingLanguageConfig, setPreparingLanguageConfig] = useState(false);
  const [languageEditor, setLanguageEditor] = useState<{
    row: RewardedAdLanguageConfigResponse | null;
    defaults?: RewardedAdConfig;
    candidates?: string[];
  } | null>(null);
  const [unitEditor, setUnitEditor] = useState<{
    placement: RewardedAdPlacement;
    row?: RewardedAdUnitResponse;
  } | null>(null);
  const [deleteLanguage, setDeleteLanguage] = useState<string | null>(null);
  const deleteMutation = useMutation({
    mutationFn: (language: string) => rewardedAdsApi.deleteLanguage(appId, language),
    onSuccess: async () => {
      setDeleteLanguage(null);
      toast('已删除语言配置 · 该语言整份回退 App 默认', { tone: 'pos' });
      await queryClient.invalidateQueries({ queryKey: rewardedAdQueryKeys.languages(appId) });
    },
    onError: async (error, language) => {
      try {
        // 写已提交但响应丢失时，以重新读取的真值对账；不改变 DELETE 不存在须报错的契约。
        await queryClient.invalidateQueries(
          { queryKey: rewardedAdQueryKeys.languages(appId) },
          { throwOnError: true },
        );
        const rows = queryClient.getQueryData<RewardedAdLanguageConfigResponse[]>(
          rewardedAdQueryKeys.languages(appId),
        );
        if (rows && !rows.some((row) => row.language === language)) {
          setDeleteLanguage(null);
          toast('已重新核对：该语言配置已不存在', { tone: 'pos' });
          return;
        }
      } catch {
        // 重取也失败时保留弹窗与原错误，不能把未知结果当成功。
      }
      toast(error instanceof Error ? error.message : '删除失败', { tone: 'neg' });
    },
  });
  const existingLanguages = new Set((languageQuery.data ?? []).map((row) => row.language));
  const newLanguages = allowedLanguages.filter((language) => !existingLanguages.has(language));
  const prepareNewLanguageConfig = async () => {
    if (!canWrite || unsavedAppChangesRef.current || preparingLanguageConfig) return;
    setPreparingLanguageConfig(true);
    try {
      const [currentApp, currentRows] = await Promise.all([
        queryClient.fetchQuery({
          queryKey: appQueryKeys.detail(appId),
          queryFn: () => getApp(appId),
          staleTime: 0,
        }),
        queryClient.fetchQuery({
          queryKey: rewardedAdQueryKeys.languages(appId),
          queryFn: () => rewardedAdsApi.listLanguages(appId),
          staleTime: 0,
        }),
      ]);
      if (unsavedAppChangesRef.current) return;
      const parsedDefaults = RewardedAdConfigSchema.safeParse(currentApp.rewarded_ad_config);
      if (!parsedDefaults.success)
        throw new Error('当前服务端未返回完整广告默认配置，请刷新后重试');
      const candidates = currentApp.allowed_languages.filter(
        (language) => !currentRows.some((row) => row.language === language),
      );
      if (candidates.length === 0) {
        toast('允许语言均已有配置，列表已更新', { tone: 'info' });
        return;
      }
      setLanguageEditor({ row: null, defaults: parsedDefaults.data, candidates });
    } catch (error) {
      toast(error instanceof Error ? error.message : '刷新当前配置失败', { tone: 'neg' });
    } finally {
      setPreparingLanguageConfig(false);
    }
  };
  // 独立保存不应误触父 App 表单 dirty；默认配置仍由父表单页头保存。
  return (
    <div onInput={(event) => event.stopPropagation()} onChange={(event) => event.stopPropagation()}>
      <Section
        title="激励广告语言独立配置"
        sub="按用户初始内容语言整份匹配，未命中整份回退 App 默认；已移出允许语言的既有行仍可编辑或删除。"
        action={
          <button
            className="btn btn-sm"
            disabled={
              !canWrite ||
              hasUnsavedAppChanges ||
              preparingLanguageConfig ||
              !languageQuery.isSuccess ||
              newLanguages.length === 0
            }
            onClick={() => void prepareNewLanguageConfig()}
          >
            新增语言配置
          </button>
        }
      >
        {hasUnsavedAppChanges && <p role="status">请先保存或放弃 App 更改，再新增语言配置。</p>}
        {languageQuery.isLoading && <p>语言配置加载中…</p>}
        {languageQuery.isError && (
          <p role="alert">
            语言配置加载失败{' '}
            <button className="btn btn-sm" onClick={() => void languageQuery.refetch()}>
              重试
            </button>
          </p>
        )}
        {languageQuery.isSuccess && languageQuery.data.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>尚无语言独立配置，全部使用 App 默认。</p>
        )}
        {languageQuery.data?.map((row) => (
          <FormRow
            key={row.language}
            label={`${fmtLang(row.language)} (${row.language})`}
            hint={
              allowedLanguages.includes(row.language)
                ? `更新于 ${fmtDateTime(row.updated_at)}`
                : '已移出允许语言 · 保留配置'
            }
          >
            <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
              <span className="mono">
                BONUS {row.config.bonus_reward} · 任务 {row.config.task_daily_limit} · 章节{' '}
                {row.config.chapter_daily_limit} · D{row.config.registration_day_threshold}
              </span>
              <button
                className="btn btn-sm"
                disabled={!canWrite}
                onClick={() => setLanguageEditor({ row })}
                aria-label={`编辑广告语言 ${row.language}`}
              >
                编辑
              </button>
              <button
                className="btn btn-sm btn-danger"
                disabled={!canWrite || deleteMutation.isPending}
                onClick={() => setDeleteLanguage(row.language)}
                aria-label={`删除广告语言 ${row.language}`}
              >
                删除
              </button>
            </div>
          </FormRow>
        ))}
      </Section>
      <Section
        title="激励广告单元"
        sub="任务中心与章节解锁各登记独立单元。单元历史归属固定，不可跨 App、平台或用途复用；替换生成新版本并保留旧映射，相同映射幂等。开发联验使用官方测试单元。"
      >
        {platform === 'WEB' ? (
          <p>WEB 不提供激励广告，禁止登记单元。</p>
        ) : (
          <>
            <p>
              固定平台：<span className="mono">{platform}</span>
            </p>
            {unitQuery.isLoading && <p>单元加载中…</p>}
            {unitQuery.isError && (
              <p role="alert">
                单元加载失败{' '}
                <button className="btn btn-sm" onClick={() => void unitQuery.refetch()}>
                  重试
                </button>
              </p>
            )}
            {unitQuery.isSuccess &&
              (['TASK_CENTER', 'CHAPTER_UNLOCK'] as const).map((placement) => {
                const row = unitQuery.data?.find((unit) => unit.placement === placement);
                return (
                  <FormRow
                    key={placement}
                    label={PLACEMENT_LABELS[placement]}
                    hint={
                      row
                        ? `版本 ${row.id} · ${fmtDateTime(row.created_at)}`
                        : '尚未登记 · 该入口关闭'
                    }
                  >
                    <div className="hstack" style={{ gap: 10, flexWrap: 'wrap' }}>
                      <span className="mono">{row?.ad_unit_id ?? '—'}</span>
                      {row && <span className="mono">SSV {row.ssv_ad_unit_id}</span>}
                      <button
                        className="btn btn-sm"
                        aria-label={`登记${PLACEMENT_LABELS[placement]}单元`}
                        disabled={!canWrite || !unitQuery.isSuccess}
                        onClick={() => setUnitEditor({ placement, row })}
                      >
                        {row ? '替换单元' : '登记单元'}
                      </button>
                    </div>
                  </FormRow>
                );
              })}
          </>
        )}
      </Section>
      {languageEditor && (
        <LanguageConfigDialog
          appId={appId}
          row={languageEditor.row}
          candidates={languageEditor.candidates ?? newLanguages}
          defaultConfig={languageEditor.defaults ?? defaultConfig}
          onClose={() => setLanguageEditor(null)}
        />
      )}
      {unitEditor && (
        <UnitConfigDialog
          appId={appId}
          platform={platform}
          placement={unitEditor.placement}
          row={unitEditor.row}
          onClose={() => setUnitEditor(null)}
        />
      )}
      {deleteLanguage && (
        <ConfirmDialog
          title="删除激励广告语言配置"
          message={`${deleteLanguage} 将整份回退 App 默认配置。`}
          confirmLabel={deleteMutation.isPending ? '删除中…' : '确认删除'}
          onClose={() => {
            if (!deleteMutation.isPending) setDeleteLanguage(null);
          }}
          onConfirm={() => {
            if (!deleteMutation.isPending) deleteMutation.mutate(deleteLanguage);
          }}
        />
      )}
    </div>
  );
}

function LanguageConfigDialog({
  appId,
  row,
  candidates,
  defaultConfig,
  onClose,
}: {
  appId: string;
  row: RewardedAdLanguageConfigResponse | null;
  candidates: string[];
  defaultConfig: RewardedAdConfig;
  onClose: () => void;
}) {
  const [language, setLanguage] = useState(row?.language ?? candidates[0] ?? '');
  const [draft, setDraft] = useState(() => rewardedAdConfigToDraft(row?.config ?? defaultConfig));
  const [errors, setErrors] = useState<RewardedAdFieldErrors>({});
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (config: RewardedAdConfig) =>
      rewardedAdsApi.replaceLanguage(appId, language, config),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rewardedAdQueryKeys.languages(appId) });
      toast('已保存完整语言配置', { tone: 'pos' });
      onClose();
    },
    onError: (error) => {
      setErrors(fieldErrorsFromError(error));
      toast(error instanceof Error ? error.message : '保存失败', { tone: 'neg' });
    },
  });
  const save = () => {
    const parsed = RewardedAdConfigSchema.safeParse(rewardedAdConfigFromDraft(draft));
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [`config.${issue.path.join('.')}`, issue.message]),
        ),
      );
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  };
  return (
    <Dialog
      title={row ? `编辑广告语言 ${language}` : '新增广告语言配置'}
      icon="IconLanguages"
      subtitle={
        row
          ? '整份替换四项参数；保存将覆盖该语言现有配置'
          : '新增预填最新 App 默认；保存时如已存在，将整份替换其四项参数'
      }
      width={680}
      onClose={() => {
        if (!mutation.isPending) onClose();
      }}
      footer={
        <>
          <button className="btn" disabled={mutation.isPending} onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-primary"
            disabled={mutation.isPending || !language}
            onClick={save}
          >
            {mutation.isPending ? '保存中…' : '保存语言配置'}
          </button>
        </>
      }
    >
      <DialogBody>
        <FormRow label="初始内容语言">
          <Select
            value={language}
            disabled={Boolean(row) || mutation.isPending}
            onChange={(event) => setLanguage(event.target.value)}
          >
            {(row ? [row.language] : candidates).map((candidate) => (
              <option key={candidate} value={candidate}>
                {fmtLang(candidate)} ({candidate})
              </option>
            ))}
          </Select>
          {errors.language && <p role="alert">{errors.language}</p>}
        </FormRow>
        <RewardedAdConfigFields
          value={draft}
          onChange={(value) => {
            setDraft(value);
            setErrors({});
          }}
          disabled={mutation.isPending}
          errors={errors}
        />
      </DialogBody>
    </Dialog>
  );
}

function UnitConfigDialog({
  appId,
  platform,
  placement,
  row,
  onClose,
}: {
  appId: string;
  platform: string;
  placement: RewardedAdPlacement;
  row?: RewardedAdUnitResponse;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState({
    ad_unit_id: row?.ad_unit_id ?? '',
    ssv_ad_unit_id: row?.ssv_ad_unit_id ?? '',
  });
  const [errors, setErrors] = useState<RewardedAdFieldErrors>({});
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: (body: typeof draft) => rewardedAdsApi.replaceUnit(appId, placement, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: rewardedAdQueryKeys.units(appId) });
      toast('广告单元已登记 · 历史映射保留', { tone: 'pos' });
      onClose();
    },
    onError: (error) => {
      setErrors(fieldErrorsFromError(error));
      toast(error instanceof Error ? error.message : '保存失败', { tone: 'neg' });
    },
  });
  const save = () => {
    const parsed = UpsertRewardedAdUnitRequestSchema.safeParse(draft);
    if (!parsed.success) {
      setErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [issue.path.join('.'), issue.message]),
        ),
      );
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data);
  };
  return (
    <Dialog
      title={`${row ? '替换' : '登记'}${PLACEMENT_LABELS[placement]}单元`}
      icon="IconLink"
      subtitle={`${platform} · 平台由 App 固定；替换仅影响新会话`}
      width={680}
      onClose={() => {
        if (!mutation.isPending) onClose();
      }}
      footer={
        <>
          <button className="btn" disabled={mutation.isPending} onClick={onClose}>
            取消
          </button>
          <button className="btn btn-primary" disabled={mutation.isPending} onClick={save}>
            {mutation.isPending ? '保存中…' : '保存单元'}
          </button>
        </>
      }
    >
      <DialogBody>
        {(['ad_unit_id', 'ssv_ad_unit_id'] as const).map((field) => (
          <FormRow
            key={field}
            label={field === 'ad_unit_id' ? '原生完整广告单元 ID' : 'SSV 数值单元 ID'}
            hint={
              field === 'ad_unit_id' ? 'ca-app-pub-发布商/单元' : '须与完整 ID 最后一段完全一致'
            }
            required
          >
            <input
              className="input mono"
              aria-label={field}
              value={draft[field]}
              disabled={mutation.isPending}
              aria-invalid={Boolean(errors[field])}
              onChange={(event) => {
                setDraft({ ...draft, [field]: event.target.value });
                setErrors({});
              }}
            />
            {errors[field] && (
              <div role="alert" style={{ color: 'var(--neg)' }}>
                {errors[field]}
              </div>
            )}
          </FormRow>
        ))}
      </DialogBody>
    </Dialog>
  );
}
